// temporary patch until vueuse has implemented the feature to use overflow: clip
import { useEventListener } from '@vueuse/core'
import {
  type Fn,
  type MaybeRefOrGetter,
  isIOS,
  toRef,
  toValue,
  tryOnScopeDispose,
} from '@vueuse/shared'
import { computed, ref, watch } from 'vue-demi'

let lockStack = 0

type HTMLElementOrWindow =
  | HTMLElement
  | SVGElement
  | Window
  | Document
  | null
  | undefined
  | typeof globalThis

const resolveElement = (element: HTMLElementOrWindow): HTMLElementOrWindow => {
  if (typeof Window !== 'undefined' && element instanceof Window)
    return element.document.documentElement

  if (typeof Document !== 'undefined' && element instanceof Document)
    return element.documentElement

  return element as HTMLElementOrWindow
}

const checkOverflowScroll = (ele: Element): boolean => {
  const style = globalThis.getComputedStyle(ele)
  if (
    style.overflowX === 'scroll' ||
    style.overflowY === 'scroll' ||
    (style.overflowX === 'auto' && ele.clientWidth < ele.scrollWidth) ||
    (style.overflowY === 'auto' && ele.clientHeight < ele.scrollHeight)
  ) {
    return true
  } else {
    const parent = ele.parentNode as Element

    if (!parent || parent.tagName === 'BODY') return false

    return checkOverflowScroll(parent)
  }
}

const preventDefault = (rawEvent: TouchEvent): boolean => {
  const event = rawEvent || globalThis.event

  const _target = event.target as Element

  // Do not prevent if element or parentNodes have overflow: scroll set.
  if (checkOverflowScroll(_target)) return false

  // Do not prevent if the event has more than one touch (usually meaning this is a multi touch gesture like pinch to zoom).
  if (event.touches.length > 1) return true

  if (event.preventDefault) event.preventDefault()

  return false
}

/**
 * Lock scrolling of the element.
 *
 * @see https://vueuse.org/useScrollLock
 * @param element
 */
export const useScrollLock = (
  element: MaybeRefOrGetter<HTMLElementOrWindow>,
  initialState = false,
  overflowType: CSSStyleDeclaration['overflow'] = 'hidden'
) => {
  const isLocked = ref(initialState)
  let stopTouchMoveListener: Fn | null = null

  watch(
    toRef(element),
    (htmlElement) => {
      const target = resolveElement(toValue(htmlElement))
      if (target) {
        const ele = target as HTMLElement
        if (isLocked.value) ele.style.overflow = overflowType
      }
    },
    {
      immediate: true,
    }
  )

  const lock = () => {
    const el = resolveElement(toValue(element)) as HTMLElement
    lockStack++

    if (!el || isLocked.value) return
    if (isIOS) {
      stopTouchMoveListener = useEventListener(
        el,
        'touchmove',
        (e) => {
          preventDefault(e as TouchEvent)
        },
        { passive: false }
      )
    }
    el.style.overflow = overflowType
    isLocked.value = true
  }

  const unlock = () => {
    lockStack--

    if (lockStack > 0) {
      return
    }

    lockStack = 0

    const el = resolveElement(toValue(element)) as HTMLElement
    if (!el || !isLocked.value) return
    isIOS && stopTouchMoveListener?.()
    el.style.overflow = ''
    isLocked.value = false
  }

  tryOnScopeDispose(unlock)

  return computed<boolean>({
    get: () => {
      return isLocked.value
    },
    set: (v) => {
      if (v) lock()
      else unlock()
    },
  })
}
